import React, { useState } from "react";
import { Typography, Chip, Box, Snackbar, Alert, Tooltip } from "@mui/material";
import CustomTable from "../../components/Table/Table";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAllActivitiesViewModel } from "./AllActivitiesViewModel.js";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import TagsIcon from "@mui/icons-material/Public"
import { useLoading } from "../../contexts/LoadingContext";
import Modal from "../../components/Modal/Modal";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import AddActivity from "./AddActivity.js";
import { useRbac } from "../../hooks/RbacContext.js";
import { getEnumLabel } from "../../utils/enumUtils.js";

const TagItem = ({ tag }) => (
    <Chip
        label={tag.tag_name}
        size="small"
        sx={{
            borderRadius: "16px",   
            marginRight: 0.5,
            marginBottom: 0.5,
            background: "#F5F6FA",
            color: "#3949AB",
            fontWeight: 500,
            fontSize: "0.78rem",
            height: "25px",        
            lineHeight: "1.3",         
            padding: "0 8px",          
            display: "flex",           
            alignItems: "center",      
        }}
        data-testid="tag-chip"
    />
);

const AllActivity = () => {
    const {
        activities,
        error,
        handleDelete,
        createActivity,
        updateActivity,
        handleSearch,
        tags,
        getTagName,
        tagsByType,
        fetchTagsByType
    } = useAllActivitiesViewModel();

    const { showLoading, hideLoading } = useLoading();

    const [isAddActivityOpen, setIsAddActivityOpen] = useState(false);
    const [selectedActivity, setSelectedActivity] = useState(null);
    const [successModal, setSuccessModal] = useState(null);
    const [deleteConfirmation, setDeleteConfirmation] = useState(null);
    const { checkAccess } = useRbac();

    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackbarMsg, setSnackbarMsg] = useState("");
    const [addActivityFormKey, setAddActivityFormKey] = useState(0);

    const columns = [
        {
            id: "activity_code",
            label: "Activity Code",
        },
        {
            id: "activity_name",
            label: "Activity Name",
            render: (value, row) => {
                const displayText = `${value}`;
                return (
                    (
                        <Tooltip title={displayText} arrow>
                            <Typography
                                variant="body2"
                                noWrap
                                sx={{
                                    fontSize: "inherit",
                                    maxWidth: 200,
                                    "& a": {
                                        color: "inherit",
                                        textDecoration: "none",
                                        "&:hover": {
                                            textDecoration: "underline",
                                        },
                                    },
                                }}
                            >

                                {displayText}

                            </Typography>
                        </Tooltip>
                    )
                );
            },
        },
        {
            id: "tag_type",
            label: "Tag Type",
            render: (value) => getEnumLabel("tag_type", value),
            sortValue: (value) => getEnumLabel("tag_type", value),
        },
        {
            id: "tags",
            label: "Tag",
            render: (tagsArr) =>
                Array.isArray(tagsArr) && tagsArr.length > 0 ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "2px 0px" }}>
                        {tagsArr.map((tag) => (
                            <TagItem key={tag.tag_id || tag.tag_name} tag={tag} />
                        ))}
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        No tags
                    </Typography>
                ),
            searchValue: (tagsArr) =>
                Array.isArray(tagsArr)
                    ? tagsArr.map((t) => t.tag_name).join(", ")
                    : "",
        }
    ];
    const fixedColumns = {
        left: [],
        right: ["actions"],
    };
    const handleAddActivity = () => {
        setSelectedActivity(null);
        setIsAddActivityOpen(true);
        setAddActivityFormKey(prev => prev + 1); // This will reset form each open
    };

    const handleEditActivity = (activity) => {
        if(activity.tag_type)
            fetchTagsByType(activity.tag_type);
        setSelectedActivity(activity);
        setIsAddActivityOpen(true);
        setAddActivityFormKey(prev => prev + 1);
    };

    const handleCloseDialog = () => {
        setIsAddActivityOpen(false);
        setSelectedActivity(null);
    };

    const handleSubmit = async (formData) => {
        try {
            showLoading();
            if (selectedActivity) {
                await updateActivity(selectedActivity.activity_id, formData);
                setSuccessModal({
                    type: "success",
                    title: "Success",
                    message: "Activity updated successfully",
                });
                handleCloseDialog();
            } else {
                await createActivity(formData);
                setShowSnackbar(true);
                setSnackbarMsg("Activity added successfully");
                setAddActivityFormKey(prev => prev + 1); // resets AddActivity
                // Do NOT close dialog after add
            }
        } catch (error) {
            setSuccessModal({
                type: "error",
                title: "Error",
                message: error.message || "Failed to add Activity",
            });
        } finally {
            hideLoading();
        }
    };

    const handleCloseSuccessModal = () => {
        setSuccessModal(null);
    };

    const handleDeleteClick = (activity) => {
        setDeleteConfirmation({
            activity,
            open: true,
        });
    };

    const handleDeleteConfirm = async () => {
        const activity = deleteConfirmation.activity;
        try {
            showLoading();
            await handleDelete(activity);
            setDeleteConfirmation(null);
            setSuccessModal({
                type: "success",
                title: "Success",
                message: "Activity deleted successfully",
            });
        } catch (error) {
            setSuccessModal({
                type: "error",
                title: "Action Failed",
                message: error.message || "Failed to delete Activity",
            });
        } finally {
            hideLoading();
        }
    };

    const getActions = (activity) => {
        const actions = [];
        if (checkAccess("activity", "update")) {
            actions.push({
                icon: <EditIcon fontSize="small" />,
                label: "Edit",
                onClick: handleEditActivity,
                color: "primary",
                sx: {
                    "&:hover": {
                        backgroundColor: (theme) =>
                            theme.palette.primary.lighter || "rgba(33, 150, 243, 0.08)",
                    },
                },
            });
        }
        if (checkAccess("activity", "delete")) {
            actions.push({
                icon: <DeleteIcon fontSize="small" />,
                label: "Delete",
                onClick: handleDeleteClick,
                color: "error",
                sx: {
                    "&:hover": {
                        backgroundColor: (theme) =>
                            theme.palette.error.lighter || "rgba(211, 47, 47, 0.08)",
                    },
                },
            });
        }

        return actions;
    };

    if (error) {
        return (
            <div>
                <Typography color="error">Error: {error}</Typography>
            </div>
        );
    }
    const columnFilters = [
        {
            id: "tag_type",
            type: "select",
            label: "Tag Type",
            icon: <TagsIcon />,
            sendIcon: true,
        }
    ];

    return (
        <div>
            <CustomTable
                columns={columns}
                data={activities}
                onAdd={handleAddActivity}
                showActions={true}
                showAddButton={checkAccess("activity", "add")}
                addButtonIcon={<PersonAddIcon />}
                showSearch={true}
                showColumnVisibility={false}
                pageSize={10}
                stickyHeader={true}
                fixedColumns={fixedColumns}
                actions={getActions}
                title="All Activities"
                subtitle="Manage and monitor all activities"
                addButtonText="Add Activity"
                alignment="left"
                fontSize="12px"
                showFilters={true}
                columnFilters={columnFilters}
            />

            <AddActivity
                key={addActivityFormKey}
                open={isAddActivityOpen}
                onClose={handleCloseDialog}
                initialData={selectedActivity}
                onSubmit={handleSubmit}
                tags={tags}
                getTagName={getTagName}
                fetchTagsByType={fetchTagsByType}
                tagsByType={tagsByType}
                onDependencyChange={(field, value, setFieldValue) => {
                    if (field === "tag_type") {
                        setFieldValue("tag_id", []);
                        if (value) {
                            fetchTagsByType(value)
                        }
                    }
                }}
            />

            {/* Snackbar for activity add success */}
            <Snackbar
                open={showSnackbar}
                autoHideDuration={2500}
                onClose={() => setShowSnackbar(false)}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert
                    onClose={() => setShowSnackbar(false)}
                    severity="success"
                    sx={{ width: "100%" }}
                >
                    {snackbarMsg}
                </Alert>
            </Snackbar>

            {successModal && (
                <Modal
                    type={successModal.type}
                    title={successModal.title}
                    message={successModal.message}
                    onClose={handleCloseSuccessModal}
                />
            )}

            {deleteConfirmation && (
                <ConfirmationModal
                    open={true}
                    title="Delete Activity"
                    message={`Are you sure you want to delete activity "${deleteConfirmation.activity.activity_name}"?`}
                    onClose={() => setDeleteConfirmation(null)}
                    onConfirm={handleDeleteConfirm}
                />
            )}
        </div>
    );
};

export default AllActivity;
